import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    waiterVisibilitySelector,
    waiterChildrenSelector,
    actions as waiterActions,
    waiterChildrenAreExistSelector,
    waiterEpisodeList
} from '../../redux/modules/waiter-of-process';

import { useOutsideClick } from 'utils/hooks/use-outside-click';

import {
    WaiterOfProcessWrapper,
    WaiterOfProcessContentWrapper,
    ContentRow
} from './waiter-of-process-styled-components';
import WaiterProgressRowClipService from './waiter-progress-row-clip-service';

// The processor and the video player code it needs stay out of the main chunk until the first episodes are queued.
// A chunk that fails to load (a network error) must not replace the whole page with the error boundary: the
// background processing is skipped and the error logged.
const NoProcessor = () => null;
const EpisodeProcessor = lazy(() =>
    import(/* webpackChunkName: "episode-processor" */ './episode-processor').catch(error => {
        // eslint-disable-next-line no-console -- the downloads wait; the page keeps working
        console.error('Loading the episode processor failed:', error);

        return { default: NoProcessor };
    })
);

const WaiterOfProcess = () => {
    const episodeList = useSelector(waiterEpisodeList);
    // Kept mounted once loaded: the processor holds the state of the tasks between the lists
    const [processorNeeded, setProcessorNeeded] = useState(false);
    useEffect(() => {
        if (episodeList?.length) setProcessorNeeded(true);
    }, [episodeList]);

    const isVisible = useSelector(waiterVisibilitySelector);
    const waiterChildren = useSelector(waiterChildrenSelector);
    const waiterChildrenAreExist = useSelector(waiterChildrenAreExistSelector);

    const dispatch = useDispatch();

    const wrapperRef = useRef(null);

    const handleClosePopup = useCallback(() => {
        dispatch(waiterActions.deActivateWaiter());
    }, [dispatch]);

    useOutsideClick({ ref: wrapperRef, callback: handleClosePopup });

    return (
        <>
            {!!processorNeeded && (
                <Suspense fallback={null}>
                    <EpisodeProcessor />
                </Suspense>
            )}
            {!!waiterChildrenAreExist ? (
                <WaiterOfProcessWrapper ref={wrapperRef} isInvisible={!isVisible}>
                    <WaiterOfProcessContentWrapper>
                        {!!waiterChildren &&
                            Object.entries(waiterChildren)
                                .filter(([id, waiterPayload]) => Boolean(id) && Boolean(waiterPayload))
                                .map(([id, waiterPayload]) => (
                                    <ContentRow key={id}>
                                        {/* eslint-disable-next-line tpe/no-props-spread -- TODO: legacy props spread (baseline) */}
                                        <WaiterProgressRowClipService {...waiterPayload} />
                                    </ContentRow>
                                ))}
                    </WaiterOfProcessContentWrapper>
                </WaiterOfProcessWrapper>
            ) : null}
        </>
    );
};

export default WaiterOfProcess;
